<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SectionLayout from '../account/components/SectionLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

// Settings state
const isEnabled = ref(false);
const logUrgent = ref(true);
const logHigh = ref(true);
const logMedium = ref(true);
const logLow = ref(true);
const logNone = ref(true);
const isSubmitting = ref(false);

// Presets state
const presets = ref([]);
const newPresetText = ref('');
const editingIndex = ref(null);
const editingText = ref('');

// Priority options for display
const priorityOptions = computed(() => [
  {
    key: 'urgent',
    label: t('PRIORITY_SETTINGS.PRIORITIES.URGENT'),
    model: logUrgent,
    icon: 'priority-urgent',
    color: 'text-ruby-11',
  },
  {
    key: 'high',
    label: t('PRIORITY_SETTINGS.PRIORITIES.HIGH'),
    model: logHigh,
    icon: 'priority-high',
    color: 'text-amber-11',
  },
  {
    key: 'medium',
    label: t('PRIORITY_SETTINGS.PRIORITIES.MEDIUM'),
    model: logMedium,
    icon: 'priority-medium',
    color: 'text-blue-11',
  },
  {
    key: 'low',
    label: t('PRIORITY_SETTINGS.PRIORITIES.LOW'),
    model: logLow,
    icon: 'priority-low',
    color: 'text-slate-11',
  },
  {
    key: 'none',
    label: t('PRIORITY_SETTINGS.PRIORITIES.NONE'),
    model: logNone,
    icon: 'priority-none',
    color: 'text-slate-9',
  },
]);

// Watch for account settings changes
watch(
  currentAccount,
  () => {
    const settings = currentAccount.value?.settings || {};
    
    isEnabled.value = settings.priority_log_enabled === true || settings.priority_log_enabled === 'true';
    logUrgent.value = settings.priority_log_urgent !== false && settings.priority_log_urgent !== 'false';
    logHigh.value = settings.priority_log_high !== false && settings.priority_log_high !== 'false';
    logMedium.value = settings.priority_log_medium !== false && settings.priority_log_medium !== 'false';
    logLow.value = settings.priority_log_low !== false && settings.priority_log_low !== 'false';
    logNone.value = settings.priority_log_none !== false && settings.priority_log_none !== 'false';
    presets.value = settings.priority_log_presets || [];
  },
  { deep: true, immediate: true }
);

// Preset management functions
const addPreset = () => {
  const text = newPresetText.value.trim();
  if (text && !presets.value.includes(text)) {
    presets.value.push(text);
    newPresetText.value = '';
  }
};

const removePreset = (index) => {
  presets.value.splice(index, 1);
};

const startEditing = (index) => {
  editingIndex.value = index;
  editingText.value = presets.value[index];
};

const saveEdit = () => {
  const text = editingText.value.trim();
  if (text && editingIndex.value !== null) {
    presets.value[editingIndex.value] = text;
  }
  cancelEdit();
};

const cancelEdit = () => {
  editingIndex.value = null;
  editingText.value = '';
};

const handleEditKeydown = (event) => {
  if (event.key === 'Enter') {
    event.preventDefault();
    saveEdit();
  } else if (event.key === 'Escape') {
    cancelEdit();
  }
};

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    await updateAccount({
      priority_log_enabled: isEnabled.value,
      priority_log_urgent: logUrgent.value,
      priority_log_high: logHigh.value,
      priority_log_medium: logMedium.value,
      priority_log_low: logLow.value,
      priority_log_none: logNone.value,
      priority_log_presets: presets.value,
    }, { silent: true });
    useAlert(t('PRIORITY_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('PRIORITY_SETTINGS.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const handleDisable = async () => {
  try {
    isSubmitting.value = true;
    await updateAccount({
      priority_log_enabled: false,
    }, { silent: true });
    useAlert(t('PRIORITY_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('PRIORITY_SETTINGS.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const toggleEnabled = () => {
  if (!isEnabled.value) {
    handleDisable();
  }
};
</script>

<template>
  <div class="flex flex-col max-w-2xl mx-auto w-full">
    <BaseSettingsHeader :title="$t('PRIORITY_SETTINGS.TITLE')" />
    
    <div class="flex-grow flex-shrink min-w-0 mt-3">
      <SectionLayout
        :title="$t('PRIORITY_SETTINGS.LOG_POPUP.TITLE')"
        :description="$t('PRIORITY_SETTINGS.LOG_POPUP.DESCRIPTION')"
        :hide-content="!isEnabled"
      >
        <template #headerActions>
          <div class="flex justify-end">
            <Switch v-model="isEnabled" @change="toggleEnabled" />
          </div>
        </template>

        <form class="grid gap-5" @submit.prevent="handleSubmit">
          <!-- Priority toggles -->
          <div class="rounded-xl border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12 divide-y divide-n-weak">
            <div
              v-for="priority in priorityOptions"
              :key="priority.key"
              class="p-3 h-12 flex items-center justify-between"
            >
              <div class="flex items-center gap-2">
                <fluent-icon
                  :icon="priority.icon"
                  size="16"
                  :class="priority.color"
                />
                <span>{{ priority.label }}</span>
              </div>
              <Switch v-model="priority.model.value" />
            </div>
          </div>

          <!-- Presets Section -->
          <div class="space-y-3">
            <div>
              <h4 class="text-sm font-medium text-slate-800 dark:text-slate-100">
                {{ $t('PRIORITY_SETTINGS.PRESETS.TITLE') }}
              </h4>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{ $t('PRIORITY_SETTINGS.PRESETS.DESCRIPTION') }}
              </p>
            </div>

            <!-- Add new preset -->
            <div class="flex gap-2">
              <Input
                v-model="newPresetText"
                :placeholder="$t('PRIORITY_SETTINGS.PRESETS.PLACEHOLDER')"
                class="flex-1"
                @keydown.enter.prevent="addPreset"
              />
              <NextButton
                slate
                icon="i-lucide-plus"
                :label="$t('PRIORITY_SETTINGS.PRESETS.ADD_BUTTON')"
                @click="addPreset"
              />
            </div>

            <!-- Presets list -->
            <div v-if="presets.length > 0" class="rounded-xl border border-n-weak bg-n-solid-1 divide-y divide-n-weak">
              <div
                v-for="(preset, index) in presets"
                :key="index"
                class="p-3 flex items-center justify-between gap-3 group"
              >
                <!-- Editing mode -->
                <template v-if="editingIndex === index">
                  <Input
                    v-model="editingText"
                    class="flex-1"
                    autofocus
                    @keydown="handleEditKeydown"
                    @blur="saveEdit"
                  />
                  <div class="flex gap-1">
                    <NextButton
                      xs
                      slate
                      icon="i-lucide-check"
                      @click="saveEdit"
                    />
                    <NextButton
                      xs
                      slate
                      icon="i-lucide-x"
                      @click="cancelEdit"
                    />
                  </div>
                </template>
                
                <!-- Display mode -->
                <template v-else>
                  <div class="flex items-center gap-2 flex-1 min-w-0">
                    <fluent-icon icon="text-quote" size="14" class="text-slate-400 flex-shrink-0" />
                    <span class="text-sm text-slate-700 dark:text-slate-200 truncate">
                      {{ preset }}
                    </span>
                  </div>
                  <div class="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <NextButton
                      xs
                      slate
                      ghost
                      icon="i-lucide-pencil"
                      @click="startEditing(index)"
                    />
                    <NextButton
                      xs
                      ruby
                      ghost
                      icon="i-lucide-trash-2"
                      @click="removePreset(index)"
                    />
                  </div>
                </template>
              </div>
            </div>

            <!-- Empty state -->
            <div
              v-else
              class="rounded-xl border border-dashed border-n-weak p-6 text-center"
            >
              <fluent-icon icon="text-quote" size="24" class="text-slate-300 dark:text-slate-600 mx-auto mb-2" />
              <p class="text-sm text-slate-500 dark:text-slate-400">
                {{ $t('PRIORITY_SETTINGS.PRESETS.EMPTY') }}
              </p>
            </div>
          </div>

          <!-- Help text -->
          <div class="rounded-md bg-blue-50 p-4 dark:bg-blue-900/20">
            <div class="flex">
              <div class="flex-shrink-0">
                <fluent-icon
                  icon="info"
                  size="20"
                  class="text-blue-600 dark:text-blue-400"
                />
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-blue-800 dark:text-blue-200">
                  {{ $t('PRIORITY_SETTINGS.HOW_IT_WORKS.TITLE') }}
                </h3>
                <div class="mt-2 text-sm text-blue-700 dark:text-blue-300">
                  <ul class="list-disc space-y-1 pl-5">
                    <li>{{ $t('PRIORITY_SETTINGS.HOW_IT_WORKS.ITEM_1') }}</li>
                    <li>{{ $t('PRIORITY_SETTINGS.HOW_IT_WORKS.ITEM_2') }}</li>
                    <li>{{ $t('PRIORITY_SETTINGS.HOW_IT_WORKS.ITEM_3') }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="flex gap-2">
            <NextButton
              blue
              type="submit"
              :is-loading="isSubmitting"
              :label="$t('PRIORITY_SETTINGS.SAVE_BUTTON')"
            />
          </div>
        </form>
      </SectionLayout>
    </div>
  </div>
</template>
